import type { JwtDecodeData } from '@/models/interfaces/token.interface'
import { type JwtPayload, jwtDecode } from 'jwt-decode'

/** 處理 JWT token 的解碼與有效性驗證 */
export const useToken = () => {
  /**
   * 使用 `jwt-decode` 解碼 JWT token，不驗證簽章（僅解析 payload）。
   * @param token - JWT 字串
   * @returns 解碼後的 payload；token 為空時回傳 `null`
   */
  const _decodeToken = (token: string): JwtPayload | null => {
    if (!token) return null

    // 只解析 payload，不在前端驗證簽章
    const decodedToken = jwtDecode(token)
    return decodedToken
  }

  /**
   * 判斷 token 是否已過期。
   * @param decodedToken - 已解碼的 JWT payload
   * @returns `true` 表示已過期或無法判斷（`exp` 欄位不存在時視為過期）
   * @remarks 以 Unix 秒為單位與當前時間比較；`Date.now()` 回傳毫秒，需除以 1000。
   */
  const _checkExpire = (decodedToken: JwtPayload | null) => {
    if (!decodedToken || !decodedToken.exp) return true

    // JWT 的 exp 是秒，需先把 Date.now() 轉成秒
    const currentTime = Date.now() / 1000
    return decodedToken.exp < currentTime
  }

  /**
   * 驗證 token 是否存在且尚未過期。
   * @param token - JWT 字串（可為 `null`）
   * @returns `true` 表示 token 有效，`false` 表示無效（為空、已過期或格式錯誤）
   * @remarks 解碼失敗（如格式損毀）時一律回傳 `false` 並記錄 error log，不拋出例外。
   */
  const checkTokenValidity = (token: string | null): boolean => {
    if (!token) return false

    try {
      const decodedToken = _decodeToken(token)
      return !_checkExpire(decodedToken)
    } catch (error) {
      // token 損毀或格式錯誤一律視為失效
      console.error('Error decoding token:', error)
      return false
    }
  }

  /**
   * 從 token payload 中取得指定欄位的值。
   * @param token - JWT 字串
   * @param key - 要取得的 payload 欄位名稱；省略時回傳完整 payload
   * @returns 指定欄位的值或完整 payload；解碼失敗時回傳 `null`
   */
  const getDataFromToken = <T extends JwtPayload | K, K = JwtDecodeData>(
    token: string,
    key?: keyof T
  ): T[keyof T] | T | null => {
    const decodedToken = _decodeToken(token) as T
    if (!decodedToken) return null

    if (key) {
      // 權限、頭像、時區等欄位直接從 token 取值
      return decodedToken[key]
    }

    return decodedToken
  }

  return {
    _decodeToken,
    checkTokenValidity,
    getDataFromToken
  }
}
